import { HomeIcon, QuestionIcon, StarIcon } from './icons'
import { useHubLang } from '../lib/i18n'
import { SHELL } from '../lib/hubCopy'
import { TOUR_NAV } from '../lib/tour'
import { badgeText } from '../lib/waiting'

/**
 * The bottom bar: three tabs, fixed, below 820px only. SHL-18.
 *
 * ── THE FAULT THIS FIXES IS NOT "THE RAIL IS UGLY ON A PHONE" ───────────────
 *
 * Below 820px `.sidebar` was `position: static` — it scrolled away with the
 * page, and the hub at 375px is 8,246px tall. So the navigation existed for the
 * first screen of ten and then did not. A rail that is present but unreachable
 * is the same as no rail, except that nobody files it, because on the screen
 * where you look for it, it is there.
 *
 * ── WHY NOT A DRAWER, WHICH IS WHAT EVERYONE SUGGESTS FIRST ─────────────────
 *
 * hub.css's own note gives the reason the rail was chosen over a hamburger:
 * "one tap to any section, no menu to discover." That reason was sound and had
 * quietly stopped being true — the rail measured 1,932px wide at 375px, of
 * which 1,254px was nine disabled SOON items, so reaching Moderation meant
 * scrolling past nine things nobody can press. SHL-17 deleted those items and
 * took an employee's rail to exactly 375px. A drawer would have re-buried a nav
 * that now fits on the screen; the honest fix is to nail it down.
 *
 * ── THREE, AND ADMIN IS NOT A FOURTH ────────────────────────────────────────
 *
 * Of 941 people, `tenant_admin` is 1, `stock_admin` 1, `stock_room_manager` 4,
 * `moderator` 5, `comms_admin` 6 — under ten see any admin item at all. A
 * fourth tab spends a quarter of the bar, for everybody, on a destination
 * almost nobody can open. The admin destinations live in the identity menu
 * instead: the tabs are where you go, the avatar is who you are and what you
 * may do. **Adding a fourth tab later means redoing this arithmetic** — it is
 * not a free slot.
 *
 * ── THE LABELS STAY, AND "ICON TABS" IS NOT AN ARGUMENT AGAINST THEM ────────
 *
 * Three tabs across 375px is 125px each, so the text costs nothing. And "Wiki
 * One" is a brand name: no glyph infers it, least of all a bare question mark
 * that honestly reads as "help" (which is also correct — HLP-1 puts the one ask
 * box on that route). An unlabelled icon bar would be a guess three times over
 * on the launch of a system nobody has used before.
 *
 * ── IT IS IN THE MARKUP AT EVERY WIDTH, HIDDEN BY CSS ───────────────────────
 *
 * Not gated on a JS breakpoint. A `matchMedia` read in a component is a second
 * copy of the 820px number that can disagree with the stylesheet, and it is
 * unreadable to the suite — an effect never runs under `renderToString`, so a
 * JS-gated bar would be a component every assertion sees as empty.
 *
 * ── `hidden` IS THE ONE EXCEPTION, AND IT IS STATE RATHER THAN A BREAKPOINT ──
 *
 * SHL-23: the bar stands down while the Ask One drawer is open. That is not a
 * width question, so CSS cannot answer it without `:has()` — and `:has()`
 * would fail silently into the old behaviour on an iOS below 15.4, which is
 * the failure mode this project keeps paying for. AppShell passes its own
 * `askOpen`, so there is exactly one answer to "is the drawer open".
 *
 * `hidden` needs `.tabbar[hidden] { display: none }` in hub.css to bite: the
 * attribute's UA `display: none` loses to `.tabbar { display: block }` in the
 * 820px block. Same shape, and the same one-line fix, as `.onedrawer[hidden]`.
 */

/** Label, route, glyph. The label matches AppShell's `current` exactly, which
 *  is what marks the active tab — there is no second source for "where am I". */
const TABS = [
  { label: 'Engagement Hub', short: 'Hub', route: '', Icon: HomeIcon },
  { label: 'Wiki One', short: 'Wiki One', route: 'wiki', Icon: QuestionIcon },
  // Star One reuses StarIcon rather than getting a tab-sized star of its own:
  // it is already the glyph on every star count in the app, and a second one
  // would be two answers to one question (rule 11, applied to a drawing).
  { label: 'Star One', short: 'Star One', route: 'stars', Icon: StarIcon },
] as const

export function TabBar({
  current,
  onNavigate,
  hidden = false,
  badgeFor,
}: {
  /** The current section's label, as AppShell knows it. */
  current: string
  onNavigate: (route: string) => void
  /**
   * True while the Ask One drawer is open (SHL-23). `hidden` rather than
   * simply not rendering the element: not rendering would take the bar out of
   * the DOM on every drawer open and put it back on every close, and the
   * suite's "the bar is in the markup at every width" assertions exist
   * precisely because a bar that comes and goes is one nobody can assert on.
   * Hidden also takes it out of the tab order, which is what you want behind
   * an open drawer.
   */
  hidden?: boolean
  /**
   * 325. What waits behind a tab (Star One: an order ready to collect) -
   * AppShell's own `badgeFor`, so the tab and the rail cannot disagree.
   * Optional: a bar with no counts is the bar it always was.
   */
  badgeFor?: (route: string) => number | undefined
}) {
  const t = SHELL[useHubLang()]
  return (
    <nav className="tabbar" aria-label={t.tabbarLabel} hidden={hidden}>
      <ul className="tabbar__list">
        {TABS.map(({ label, short, route, Icon }) => {
          const active = label === current
          const count = badgeFor?.(route) ?? 0
          return (
            <li key={label}>
              <button
                type="button"
                className={`tabbar__tab${active ? ' tabbar__tab--active' : ''}`}
                aria-current={active ? 'page' : undefined}
                // SHL-34: the tour points here on a phone, at the rail's twin above 820px.
                data-tour={TOUR_NAV[route]}
                onClick={() => onNavigate(route)}
                aria-label={count > 0 ? `${route === '' ? t.hubShort : short}, ${t.waiting(count)}` : undefined}
              >
                <span className="tabbar__icon">
                  <Icon size={24} />
                  {count > 0 && (
                    <span className="cornercount" aria-hidden="true">
                      {badgeText(count)}
                    </span>
                  )}
                </span>
                {/* Only the hub's short label is a word; the other two are brand
                    names and stay as TABS writes them (lib/hubCopy.ts). */}
                <span className="tabbar__label">{route === '' ? t.hubShort : short}</span>
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
